import React from 'react';
import { Badge } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import { format } from 'date-fns';
import { useCalibrationUpdatesQuery } from '../../../../__generated__/graphql';
import { useNamespacedTranslation } from '../../../../../../hooks/useNamespacedTranslation';
import { getRateColor } from '../calibrationColors';
import { useCalibrationWorkspace } from '../CalibrationWorkspaceContext';

type Props = {
  employeeTopicId: string;
  topicType: string | null;
};

const i18Path = 'performanceReview.calibration.sidePanel';

const RateBadge: React.FC<
  React.PropsWithChildren<{
    label: string;
    rate?: number | null;
    options: ReadonlyArray<{ value: number }>;
  }>
> = ({ label, rate, options }) => {
  const color = getRateColor(rate, options);
  return (
    <Badge
      bg={color ? '' : 'light'}
      text={color ? undefined : 'dark'}
      style={
        color ? { backgroundColor: color.bg, color: color.text } : undefined
      }
      className="fw-normal">
      {label}
    </Badge>
  );
};

export const CalibrationHistory: React.FC<React.PropsWithChildren<Props>> = ({
  employeeTopicId,
  topicType,
}) => {
  const t = useNamespacedTranslation(i18Path);
  const { calibrationOptions } = useCalibrationWorkspace();
  const { data, loading } = useCalibrationUpdatesQuery({
    variables: { employeeTopicId },
    fetchPolicy: 'cache-and-network',
  });

  if (loading && !data) {
    return (
      <div>
        <div className="border-bottom pb-3 mb-3">
          <Skeleton width="60%" height={14} className="mb-2" />
          <Skeleton width="80%" height={14} className="mb-2" />
          <Skeleton width="50%" height={14} />
        </div>
        <Skeleton count={3} height={48} className="mb-3" />
      </div>
    );
  }

  const employeeTopic = data?.calibrationEmployeeTopic;
  const updates = employeeTopic?.calibrationUpdates ?? [];
  const initialRate = employeeTopic?.flair__Initial_Rate__c;
  const description = employeeTopic?.flair__Target_Object_Description__c;

  const getRateLabel = (rate: number | null | undefined): string | null => {
    if (rate == null) return null;
    return (
      calibrationOptions.find((o) => o.value === rate)?.label ?? String(rate)
    );
  };

  const initialRateLabel = getRateLabel(initialRate);

  const topicTypeLabel = topicType
    ? t(`topicType.${topicType}`, { defaultValue: topicType })
    : null;

  return (
    <div>
      {/* Info header block */}
      <div className="border-bottom pb-3 mb-3">
        {topicType && (
          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted small">{t('targetType')}</span>
            <span className="small">{topicTypeLabel}</span>
          </div>
        )}
        {description && (
          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted small">{t('targetDescription')}</span>
            <span className="small">{description}</span>
          </div>
        )}
        <div className="d-flex justify-content-between align-items-center">
          <span className="text-muted small">{t('initialRate')}</span>
          {initialRateLabel ? (
            <RateBadge
              label={initialRateLabel}
              rate={initialRate}
              options={calibrationOptions}
            />
          ) : (
            <span className="text-muted small">{t('notRated')}</span>
          )}
        </div>
      </div>

      {/* Timeline */}
      {updates.length === 0 ? (
        <p className="text-muted text-center py-4 mb-0">{t('noHistory')}</p>
      ) : (
        updates.map((update) => {
          const rateLabel = getRateLabel(update.flair__Rate__c);
          return (
            <div key={update.Id} className="border-bottom py-3">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <strong className="small">
                    {update.participant.employee.Name}
                  </strong>
                  <span className="text-muted ms-2 small">
                    {format(new Date(update.CreatedDate), 'MMM d, yyyy HH:mm')}
                  </span>
                </div>
                {rateLabel && (
                  <RateBadge
                    label={rateLabel}
                    rate={update.flair__Rate__c}
                    options={calibrationOptions}
                  />
                )}
              </div>
              {update.flair__Notes__c && (
                <p className="text-muted small mt-1 mb-0">
                  {update.flair__Notes__c}
                </p>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};
